import {
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UserContext } from '../../context';
import { SourceStoryInfo, Story, StoryStatus, StoryType, useStoryByIdQuery } from '../../services';
import {
  Avatar,
  COLLAPSE_STORAGE_KEY,
  CollapseMode,
  CollapseSwitcher,
  CreateCardAdmin,
  Deeplink,
  DeeplinkType,
  DropdownMenu,
  getAppTabActionByStoryTabType,
  getCssVar,
  getWebAppUrl,
  IconLabel,
  ImageGallery,
  Popup,
  PublishToggle,
  StoryTabType,
  StoryTags,
  useAnalytics,
  useAppDispatch,
  useAppTabsLabel,
  useBeforeUnload,
  useDuration,
  useHasHistory,
} from '../../shared';
import { togglePublishOnStory } from '../../slices';
import { NoContent } from '../Error';
import { CreateStoryModal, DeleteStoryModal } from '../StoryTabListAdmin';
import { SourceStoriesModal } from './SourceStoriesModal';
import { StoryFeedAdmin } from './StoryFeedAdmin';

import classNames from 'classnames';
import dropdownMenuClasses from '../../shared/components/DropdownMenu/DropdownMenu.module.scss';
import classes from './StoryFeedItemsAdmin.module.scss';

interface StoryFeedItemsAdminProps {
  storyTabType?: StoryTabType;
}

export const StoryFeedItemsAdmin: FunctionComponent<StoryFeedItemsAdminProps> = memo(
  ({ storyTabType }) => {
    const { t } = useTranslation();

    const { userProfile, userInfo } = useContext(UserContext);

    const { channelId } = userInfo.userData;

    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const { getAppTabsLabel } = useAppTabsLabel();

    const { logClickOpenStory } = useAnalytics();

    const { startTrackTime, getDuration } = useDuration();

    const storyId = Number(useParams().storyId);

    const [story, setStory] = useState<Story | undefined>(undefined);

    const { data: storyData, isFetching } = useStoryByIdQuery({ storyId });

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const [isUpdateOpen, setIsUpdateOpen] = useState(false);

    const [publishToggling, setPublishToggling] = useState(false);

    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    const [isSourcesModalOpen, setIsSourcesModalOpen] = useState<boolean>(false);

    const [collapseMode, setCollapseMode] = useState(
      localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD
    );

    const hasHistory = useHasHistory();

    useEffect(() => {
      window.addEventListener('storage', () => {
        setCollapseMode(localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD);
      });
    }, []);

    useEffect(() => {
      setStory(undefined);
    }, [storyId]);

    useEffect(() => {
      if (!isFetching && !story && storyData) {
        setStory(storyData);
      }
    }, [isFetching, story, storyData]);

    const logClickOpen = useCallback(
      () =>
        logClickOpenStory({
          channel_id: channelId,
          story_id: storyId,
          mix_id: storyId,
          time_stamp: new Date().toISOString(),
          duration: getDuration(),
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [channelId, logClickOpenStory, storyId]
    );

    useEffect(() => {
      startTrackTime();
      return () => logClickOpen();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logClickOpen]);

    useBeforeUnload(logClickOpen);

    const storyTabPath = useMemo(() => `/admin/${storyTabType ?? 'storyTab'}`, [storyTabType]);

    const storyTabTitle = useMemo(() => {
      if (!storyTabType) {
        return t('adminLayoutMenu.mixes');
      }

      return getAppTabsLabel(getAppTabActionByStoryTabType(storyTabType));
    }, [getAppTabsLabel, storyTabType, t]);

    const isPublished = useMemo(() => story?.status === StoryStatus.PUBLISHED, [story?.status]);

    const sources = useMemo(() => story?.sources ?? [], [story?.sources]);

    const hasSources = useMemo(() => Boolean(sources.length), [sources.length]);

    const status = useMemo(
      () => (isPublished ? StoryStatus.UNPUBLISHED : StoryStatus.PUBLISHED),
      [isPublished]
    );

    const storyStatusChange = useCallback(async () => {
      if (!story) {
        return;
      }

      setPublishToggling(true);

      try {
        const updatedStory = await toast.promise(
          dispatch(togglePublishOnStory({ storyId, fields: { status } })).unwrap(),
          {
            pending: t(`publishToggle.${status}-toggling`),
            success: t(`publishToggle.${status}-toggled`),
            error: t(`publishToggle.${status}-error`),
          }
        );

        updatedStory && setStory({ ...story, status });
      } catch (_) {
        toast(t(`publishToggle.${status}-error`));
      } finally {
        setPublishToggling(false);
      }
    }, [dispatch, status, story, storyId, t]);

    const openSource = useCallback(
      ({ channel: { subdomain }, story: { id } }: SourceStoryInfo) => {
        window.open(
          `${getWebAppUrl(subdomain)}${storyTabPath}/${id}/items`,
          '_blank',
          'noreferrer'
        );
      },
      [storyTabPath]
    );

    const sourceStoryClick = useCallback(() => {
      if (sources.length === 1) {
        openSource(sources[0]);
        return;
      }

      setIsSourcesModalOpen(true);
    }, [openSource, sources]);

    if (isFetching) {
      return <Skeleton height={'10rem'} />;
    }

    if (!story) {
      return <NoContent />;
    }

    const { title, subtitle, image, author, type } = story;

    const { url: imageUrl } = { ...image };

    const { id, avatar, screenName } = { ...author };

    const renderImage = Boolean(imageUrl && collapseMode === CollapseMode.STANDARD);

    const openProfile = () => {
      navigate(`/admin/profile${id !== userProfile.id ? `/${id}` : ''}`);
    };

    return (
      <div className={classes['feed-items']}>
        <div className={classes['feed-items__header']}>
          {hasHistory && (
            <IconLabel
              iconId={'arrow-rounded-left'}
              label={storyTabTitle}
              className={classes['feed-items__header-back']}
              labelClassName={classes['feed-items__header-back-title']}
              onClick={() => navigate(-1)}
              color={'#43445b'}
              singleColor
            />
          )}
          <CollapseSwitcher
            storageKey={COLLAPSE_STORAGE_KEY}
            className={classes['feed-items__header-switcher']}
          />
        </div>

        <div className={classNames(`story-${storyId}`, classes['feed-items__story'])}>
          {renderImage && <ImageGallery items={[{ image }]} />}

          <div className={classes['feed-items__story-content']}>
            <div className={classes['feed-items__story-author']} onClick={openProfile}>
              <Avatar url={avatar?.url} nonClickable />
              <div className={classes['feed-items__story-author-info']}>
                <div className={classes['feed-items__story-author-info-label']}>
                  {t('common.author')}
                </div>
                <div className={classes['feed-items__story-author-info-name']}>{screenName}</div>
              </div>
            </div>

            <div className={classes['feed-items__story-headlines']}>
              <h1 className={classes['feed-items__story-headlines-title']}>{title}</h1>
              {subtitle && (
                <div className={classes['feed-items__story-headlines-subTitle']}>{subtitle}</div>
              )}
            </div>

            <StoryTags story={story} />

            <div className={classes['feed-items__story-buttons']}>
              <PublishToggle
                isPublished={isPublished}
                onClick={storyStatusChange}
                disabled={publishToggling}
              />
              <IconLabel
                label={t('common.delete')}
                color={getCssVar('--base-link-text-color')}
                hoverColor={getCssVar('--base-link-text-hover-color')}
                onClick={() => setIsDeleteOpen(true)}
              />
              <IconLabel
                label={t('common.edit')}
                color={getCssVar('--base-link-text-color')}
                hoverColor={getCssVar('--base-link-text-hover-color')}
                onClick={() => setIsUpdateOpen(true)}
              />
              {hasSources && (
                <IconLabel
                  labelFirst
                  iconId={'arrow-rounded-right-up-box'}
                  iconSize={14}
                  label={t('common.source-story')}
                  color={getCssVar('--base-link-text-color')}
                  hoverColor={getCssVar('--base-link-text-hover-color')}
                  onClick={sourceStoryClick}
                />
              )}
              <Popup
                isOpen={isMenuOpen}
                setIsOpen={setIsMenuOpen}
                iconId={'dots-menu'}
                ariaLabel={t('common.more-options')}
                color={getCssVar('--action-pane-icon-color')}
                hoverColor={getCssVar('--action-pane-icon-hover-color')}
                parentClassName={classes['feed-items__story-buttons-menu']}
                bodyTop={'1.5rem'}
                bodyRight={'0'}
                body={
                  <DropdownMenu
                    width={'10rem'}
                    content={
                      <Deeplink
                        type={DeeplinkType.STORY}
                        storyId={storyId}
                        onClick={() => setIsMenuOpen(false)}
                        className={dropdownMenuClasses['dropdown-menu__item--small']}
                      />
                    }
                  />
                }
              />
            </div>
          </div>
        </div>

        {Boolean(type !== StoryType.READONLY) && <CreateCardAdmin story={story} />}

        <StoryFeedAdmin storyId={storyId} />

        {isDeleteOpen && (
          <DeleteStoryModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            storyId={storyId}
            navigateUrl={storyTabPath}
          />
        )}

        {isUpdateOpen && (
          <CreateStoryModal
            isOpen={isUpdateOpen}
            onClose={() => setIsUpdateOpen(false)}
            story={story}
            storyTabType={storyTabType}
            callback={setStory}
          />
        )}

        <SourceStoriesModal
          isOpen={isSourcesModalOpen}
          sources={sources}
          onSourceClick={openSource}
          onClose={() => setIsSourcesModalOpen(false)}
        />
      </div>
    );
  }
);
